import { Component, Host, Prop, Watch, h } from '@stencil/core';

import { renderResponsiveImages } from '../../helpers/helpers';
import type {
  CssClassMap,
  MarketAddress,
  MarketOpenTimes,
  MarketOpenTimesInfo,
  MarketSpecialOpenTimes,
  ResponsiveImages,
  nearStores,
  searchStore,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-market-info-box',
  styleUrl: 'cms-market-info-box.scss',
  shadow: false,
  scoped: true,
})
export class CmsMarketInfoBox {
  private addressObj: MarketAddress;
  private openTimesObj: MarketOpenTimesInfo;

  private searchStoresObj: searchStore;

  private responsiveImageObj: ResponsiveImages;

  private readonly marketImageSizes = `
(max-width: 767px) and (min-resolution: 288dpi) 50vw,
(max-width: 767px) and (min-resolution: 192dpi) 50vw,
(max-width: 767px) and (min-resolution: 96dpi) 100vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 33vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 33vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 50vw,
(min-width: 1280px)  550px,
 100vw`;

  /**
   * Object with Market Address data
   */
  @Prop() address: MarketAddress | string;

  /**
   * Object with Market open Times data
   */
  @Prop() openTimes: MarketOpenTimesInfo | string;

  /**
   * Object with near stores & search store url
   */
  @Prop() searchStores: searchStore | string;

  /**
   * set search stores box visible ore hidden
   */
  @Prop() hiddenSearchStores: boolean;

  /**
   * set the responsive Market image for Breakpoints 320, 480, 768,992,1200
   */
  @Prop() marketImage: ResponsiveImages | string;

  /**
   * set the image alt text
   */
  @Prop() imageAltText = 'Hornbach';
  /**
   * set true or false when is in CMS Preview
   */
  @Prop() isPreview: boolean;

  /**
   * set the Source for the Static Image Map from Google
   */
  @Prop() staticMapUrl: string;

  /**
   * set the link to Google-Maps
   */
  @Prop() googleMapsUrl: string;

  @Watch('address')
  private addressWatcher(newValue: MarketAddress | string) {
    if (newValue) {
      this.addressObj =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as MarketAddress)
          : newValue;
    }
  }

  @Watch('openTimes')
  private openTimesWatcher(newValue: MarketOpenTimesInfo | string) {
    if (newValue) {
      this.openTimesObj =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as MarketOpenTimesInfo)
          : newValue;
    }
  }

  @Watch('searchStores')
  private searchStoresWatcher(newValue: searchStore | string) {
    if (newValue) {
      this.searchStoresObj =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as searchStore)
          : newValue;
    }
  }

  @Watch('marketImage')
  private imagesWatcher(newValue: ResponsiveImages | string) {
    if (newValue) {
      this.responsiveImageObj =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as ResponsiveImages)
          : newValue;
    }
  }

  private textLinkClassMap(): CssClassMap {
    return {
      'market-info-link': true,
    };
  }

  private addressCardClassMap(): CssClassMap {
    return {
      'grid-item': true,
      'info-card': true,
      address: true,
      'hidden-search-stores': this.hiddenSearchStores,
    };
  }

  private openTimesCardClassMap(): CssClassMap {
    return {
      'grid-item': true,
      'info-card': true,
      'open-times': true,
      'hidden-search-stores': this.hiddenSearchStores,
    };
  }

  private openUrl(url: string) {
    const win = window.open(url, '_self');
    if (win != null) {
      win.focus();
    }
  }

  private renderSearchStores() {
    if (this.searchStoresObj) {
      const {
        headline,
        buttonLabel,
        searchPageUrl,
        nearStoresLabel,
        nearStores,
      } = this.searchStoresObj;

      return (
        <div class={'grid-item info-card search-stores'}>
          <div class={'label store-label'}>{headline}</div>

          <cms-button
            style={{
              'theme-text-color': 'var(--cms-semanticColors-base-strong-icon)',
            }}
            class='search-button'
            onCmsButtonClick={() => this.openUrl(searchPageUrl)}
            type='button'
            size='button-md'
            theme='secondary'
            label={buttonLabel}
          />

          {nearStores.length > 0 ? (
            <div class={'box-headline'}>
              <div class={'label'}>{nearStoresLabel}</div>
            </div>
          ) : null}

          <div class={'near-stores'}>{this.mapNearStores(nearStores)}</div>
        </div>
      );
    }
  }

  private mapNearStores(nearStores: nearStores[]) {
    if (nearStores !== undefined && nearStores.length > 0) {
      return nearStores.map((nearStore: nearStores) => {
        const { storeUrl, storeCity, storeDistance } = nearStore;
        return (
          <div key={storeCity} class={'store-wrapper'}>
            <a href={storeUrl} class={this.textLinkClassMap()} target={'_self'}>
              {storeCity}
            </a>
            <span class={'store-distance'}> ({storeDistance} km) </span>
          </div>
        );
      });
    }
  }

  private renderAddress() {
    if (this.addressObj) {
      const {
        city,
        street,
        zipCode,
        phone,
        phoneNumberFormatted,
        mapUrl,
        mapUrlLabel,
        contactUrl,
        contactLabel,
      } = this.addressObj;
      return (
        <div
          style={{
            '--cms-theme-text-color':
              'var(--cms-semanticColors-base-strong-text)',
          }}
          class={this.addressCardClassMap()}
        >
          <div class={'label'}>{city || ''}</div>
          <div class={'address-wrapper'}>
            <div>{street || ''} </div>
            <span>{zipCode || ''} </span>
            <span>{city || ''}</span>
          </div>

          <div class={'phone-wrapper'}>
            <cms-icon
              size='sm'
              icon='phone'
              display='flex'
              colorToken='--cms-semanticColors-base-strong-icon'
            />
            <cms-link
              href={phone || ''}
              link-title={phoneNumberFormatted || ''}
              content={phoneNumberFormatted || ''}
              target='_blank'
              type='standalone'
            />
          </div>
          <div class={'mail-wrapper'}>
            <cms-icon
              style={{ verticalAlign: 'middle' }}
              size='sm'
              icon='mail'
              display='flex'
              colorToken='--cms-semanticColors-base-strong-icon'
            />
            <cms-link
              href={contactUrl || '#'}
              link-title={contactLabel || ''}
              content={contactLabel || ''}
              target='_blank'
              type='standalone'
            />
          </div>
          <div class={'locator-wrapper'}>
            <cms-icon
              size='sm'
              icon='locator'
              display='flex'
              colorToken='--cms-semanticColors-base-strong-icon'
            />
            <cms-link
              href={mapUrl || '#'}
              link-title={mapUrlLabel}
              content={mapUrlLabel || ''}
              target='_blank'
              type='standalone'
            />
          </div>
        </div>
      );
    }
  }

  private mapOpenTimes(openTimes: MarketOpenTimes[]) {
    if (openTimes) {
      return openTimes.map((openTime) => {
        const { day, time } = openTime;
        return (
          <div key={day} class={'open-times-wrapper'}>
            <span class={'open-days'}>
              <strong>{day}</strong>
            </span>
            <span class={'open-times'}>
              {time} {this.openTimesObj.timesLabel}
            </span>
          </div>
        );
      });
    }
  }

  private mapOpenSpecialTimes(specialOpenTimes: MarketSpecialOpenTimes[]) {
    if (specialOpenTimes !== undefined) {
      return specialOpenTimes.map((openTime) => {
        const {
          timesLabel,
          open,
          closedLabel,
          specialDateLabel,
          timeFrom,
          timeTo,
        } = openTime;
        return (
          <div key={timesLabel} class={'special-times-wrapper'}>
            <span class={'date-label'}>
              <strong>{specialDateLabel}</strong>
            </span>
            <span class={'times-label'}>
              {open
                ? `${timeFrom} - ${timeTo} ${timesLabel}`
                : `${closedLabel}`}
            </span>
          </div>
        );
      });
    }
  }

  private renderOpenTimes() {
    if (this.openTimesObj) {
      const { openTimesHeadline, holidaysLabel, openTimes, openSpecialTimes } =
        this.openTimesObj;
      return (
        <div class={this.openTimesCardClassMap()}>
          <div class={'label'}>{openTimesHeadline || ''}</div>
          <div class={'open-time-container'}>
            {this.mapOpenTimes(openTimes)}
          </div>
          <div class={'special-times-container'}>
            {this.mapOpenSpecialTimes(openSpecialTimes)}
          </div>
          <div class={'holiday-label'}>
            <strong>{holidaysLabel || ''}</strong>
          </div>
        </div>
      );
    }
  }

  private renderMarketImage() {
    return (
      <div class={'grid-item market-image-wrapper'}>
        {this.responsiveImageObj != null
          ? renderResponsiveImages(
              this.responsiveImageObj,
              this.marketImageSizes,
              this.imageAltText,
              'market-image',
            )
          : ''}
      </div>
    );
  }

  private renderStaticMap() {
    return (
      <div class={'grid-item static-map-wrapper'}>
        <div
          class={'static-map-container'}
          data-hdp-container-id={'static-map-container'}
        />
        <a
          rel='noopener noreferrer'
          aria-label='googlemaps-link'
          href={this.googleMapsUrl ?? null}
          class='google-maps-link'
          target='_blank'
        >
          {this.isPreview ? (
            <img
              class={'static-map-image'}
              alt={'static-map'}
              width={'600'}
              height={'400'}
              loading='lazy'
              decoding='async'
              src={this.staticMapUrl}
            />
          ) : (
            <img
              alt={'static-map'}
              class={'static-map-image'}
              width={'600'}
              height={'400'}
              loading='lazy'
              decoding='async'
              data-use-hdp='true'
              data-hdp-container-ref='static-map-container'
              data-usercentrics='Google Maps'
              data-hdp-src={this.staticMapUrl}
            />
          )}
        </a>
      </div>
    );
  }

  componentWillLoad() {
    if (this.address) {
      this.addressWatcher(this.address);
    }

    if (this.openTimes !== undefined) {
      this.openTimesWatcher(this.openTimes);
    }

    if (this.searchStores !== undefined) {
      this.searchStoresWatcher(this.searchStores);
    }

    if (this.marketImage !== undefined) {
      this.imagesWatcher(this.marketImage);
    }
  }

  render() {
    return (
      <Host>
        <div class={'market-content'}>
          {this.address ? this.renderAddress() : null}
          {this.openTimes ? this.renderOpenTimes() : null}
          {this.searchStores && !this.hiddenSearchStores
            ? this.renderSearchStores()
            : null}
          {this.marketImage ? this.renderMarketImage() : null}
          {this.staticMapUrl ? this.renderStaticMap() : null}
        </div>
      </Host>
    );
  }
}
